import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Feather, Upload, FileText, Users, Lightbulb, Sparkles, BookOpen,
  Send, Mic, Volume2, Image, Download, ChevronDown, ChevronUp,
  CheckCircle, AlertCircle, Loader2, Play, Pause, RefreshCw,
  PenTool, MessageSquare, Wand2, Globe, BookMarked, Headphones, Languages
} from "lucide-react";
import AudiobookSection from "./components/AudiobookSection";
import TranslationSection from "./components/TranslationSection";
import ImageCreatorSection from "./components/ImageCreatorSection";
import LandingPage from "./components/LandingPage";
import { HowItWorks, HandoffCard } from "./components/StudioHelp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Writing Types
const WRITING_TYPES = [
  { id: "memoir", name: "Memoir", icon: BookOpen },
  { id: "fiction", name: "Fiction", icon: BookMarked },
  { id: "poetry", name: "Poetry/Slam", icon: Feather },
  { id: "lyrics", name: "Song Lyrics", icon: Volume2 },
  { id: "screenplay", name: "Screenplay", icon: FileText },
  { id: "essay", name: "Essay", icon: PenTool },
  { id: "blog", name: "Blog Post", icon: Globe },
  { id: "children", name: "Children's Book", icon: Sparkles },
  { id: "general", name: "Other", icon: FileText }
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false, step, completed }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="journey-step" data-testid={`step-${step}`}>
      <div 
        className={`step-header ${isOpen ? 'active' : ''} ${completed ? 'completed' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`step-header-${step}`}
      >
        <div className="step-header-left">
          <div className="step-number">{step}</div>
          <Icon className="step-icon" />
          <h2 className="step-title">{title}</h2>
          {completed && <CheckCircle className="completed-icon" />}
        </div>
        {isOpen ? <ChevronUp className="toggle-icon" /> : <ChevronDown className="toggle-icon" />}
      </div>
      <div className={`step-content ${isOpen ? 'open' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// Chat Component
const ChatBox = ({ step, context, onContextUpdate }) => {
  // Artistic, soulful greetings for each creative stage
  const getGreeting = (currentStep) => {
    const greetings = {
      upload: "Welcome to your creative space. ✨ Whether you're bringing a finished manuscript or the first spark of an idea, this is where your words find their home. What are you working on today?",
      analysis: "Every piece of writing has its own heartbeat. Let's listen to yours together — I'll help you see the strengths you might not notice and the places where your voice can shine even brighter.",
      characters: "Characters are the souls that breathe life into stories. Tell me who's living in your imagination — I'd love to help you discover the depths they're waiting to reveal.",
      brainstorm: "This is where magic happens — the messy, beautiful space where ideas collide and something new is born. What's stirring in your mind? Let's explore it together, without judgment.",
      polish: "Your words have power. Now let's refine them until they sing exactly the way you hear them in your heart. What would you like to transform?",
      audiobook: "There's something profound about hearing your words spoken aloud — they take on new life, new breath. Let's give your creation a voice that honors what you've built.",
      translation: "Art transcends borders. Your story deserves to reach hearts in every language. Where would you like your words to travel?",
      image: "Your words deserve a face. Whether it's a book cover, an album sleeve, or art for a single poem — let's translate the feeling of your work into something the eye can hold. What's the image already living in your imagination?",
      publish: "You've poured yourself into this work. Now it's time to share it with the world. I'm here to help you navigate that journey — from query letters to finding the right home for your creation."
    };
    return greetings[currentStep] || "I'm here with you in this creative space. What's on your mind?";
  };

  const [messages, setMessages] = useState([
    { role: 'assistant', content: getGreeting(step) }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `${step}-${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        context: context || '',
        step: step,
        session_id: sessionId
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting. Please try again!" }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-box" data-testid={`chat-${step}`}>
      <div className="chat-header">
        <MessageSquare size={18} />
        <span>Your Creative Companion</span>
      </div>
      <div className="chat-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.role}`}>
            {msg.role === 'assistant' && <Sparkles className="ai-icon" size={16} />}
            <div className="message-content">{msg.content}</div>
          </div>
        ))}
        {isLoading && (
          <div className="chat-message assistant">
            <Loader2 className="ai-icon spinning" size={16} />
            <div className="message-content">Reflecting...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input-container">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Share what's on your mind..."
          className="chat-input"
          data-testid={`chat-input-${step}`}
        />
        <button 
          onClick={sendMessage} 
          disabled={isLoading || !input.trim()}
          className="chat-send-btn"
          data-testid={`chat-send-${step}`}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  // Landing vs Studio toggle (persisted)
  const [showLanding, setShowLanding] = useState(() => {
    try {
      return localStorage.getItem('sf_skip_landing') !== 'true';
    } catch {
      return true;
    }
  });

  const enterStudio = () => {
    try { localStorage.setItem('sf_skip_landing', 'true'); } catch {}
    setShowLanding(false);
  };

  const backToLanding = (anchorId = null) => {
    try { localStorage.removeItem('sf_skip_landing'); } catch {}
    if (anchorId) {
      try { sessionStorage.setItem('sf_landing_anchor', anchorId); } catch {}
    }
    setShowLanding(true);
  };

  // Scroll to top after the landing/studio toggle re-renders, so the browser
  // doesn't keep the previous scroll offset (which makes shorter pages land partway down).
  useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration on this view-swap
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const anchorId = (() => {
      try { return sessionStorage.getItem('sf_landing_anchor'); } catch { return null; }
    })();
    if (showLanding && anchorId) {
      try { sessionStorage.removeItem('sf_landing_anchor'); } catch {}
      // Wait for landing to render then scroll to the anchor
      requestAnimationFrame(() => {
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
    } else {
      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }, [showLanding]);

  // State
  const [manuscript, setManuscript] = useState('');
  const [writingType, setWritingType] = useState('general');
  const [title, setTitle] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [characterName, setCharacterName] = useState('');
  const [characterDetails, setCharacterDetails] = useState('');
  const [generatedCharacter, setGeneratedCharacter] = useState(null);
  const [isGeneratingCharacter, setIsGeneratingCharacter] = useState(false);
  const [brainstormTopic, setBrainstormTopic] = useState('');
  const [brainstormDetails, setBrainstormDetails] = useState('');
  const [brainstormResult, setBrainstormResult] = useState(null);
  const [isBrainstorming, setIsBrainstorming] = useState(false);
  const [rewriteStyle, setRewriteStyle] = useState('enhanced');
  const [rewriteResult, setRewriteResult] = useState(null);
  const [isRewriting, setIsRewriting] = useState(false);
  const [queryLetter, setQueryLetter] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // File upload handler - now supports DOCX, PDF, TXT
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    setUploadError(null);
    
    // For plain text files, read directly
    if (filename.endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setManuscript(event.target.result);
        // Try to extract title from filename
        const titleFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/_/g, " ");
        if (!title) setTitle(titleFromFile);
      };
      reader.readAsText(file);
      return;
    }
    
    // For DOCX and PDF, send to backend for parsing
    if (filename.endsWith('.docx') || filename.endsWith('.pdf') || filename.endsWith('.rtf')) {
      setIsUploading(true);
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/document/import`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          setManuscript(response.data.text);
          // Try to extract title from filename
          const titleFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/_/g, " ");
          if (!title) setTitle(titleFromFile);
          
          // Show success message with stats
          const stats = response.data.stats;
          console.log(`Imported: ${stats.word_count} words, ${stats.paragraph_count} paragraphs`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadError(error.response?.data?.detail || 'Failed to import document. Please try again.');
      } finally {
        setIsUploading(false);
      }
      return;
    }
    
    // Unsupported format
    setUploadError('Unsupported file format. Please upload .docx, .pdf, .txt, or .rtf files.');
  };

  // Export manuscript
  const exportManuscript = async (format) => {
    if (!manuscript.trim()) {
      alert('No manuscript to export!');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/document/export/${format}`, {
        text: manuscript,
        title: title || 'Manuscript',
        author: 'Author',
        format: format
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(title || 'manuscript').replace(/\s+/g, '_')}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Analyze manuscript
  const analyzeManuscript = async () => {
    if (!manuscript.trim()) {
      alert('Please enter or upload your manuscript first!');
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/analyze`, {
        content: manuscript,
        writing_type: writingType,
        analysis_depth: 'full'
      });
      
      setAnalysisResult(response.data);
      setCompletedSteps(prev => new Set([...prev, 1]));
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Generate character
  const generateCharacter = async () => {
    if (!characterName.trim()) {
      alert('Please enter a character name!');
      return;
    }
    
    setIsGeneratingCharacter(true);
    try {
      const response = await axios.post(`${API}/character/generate`, {
        name: characterName,
        details: characterDetails,
        writing_type: writingType
      });
      
      setGeneratedCharacter(response.data);
      setCompletedSteps(prev => new Set([...prev, 3]));
    } catch (error) {
      console.error('Character error:', error);
      alert('Character generation failed. Please try again.');
    } finally {
      setIsGeneratingCharacter(false);
    }
  };

  // Brainstorm
  const startBrainstorm = async () => {
    if (!brainstormTopic.trim()) {
      alert('Please enter a topic to brainstorm!');
      return;
    }
    
    setIsBrainstorming(true);
    try {
      const response = await axios.post(`${API}/brainstorm`, {
        topic: brainstormTopic,
        writing_type: writingType,
        details: brainstormDetails
      });
      
      setBrainstormResult(response.data);
      setCompletedSteps(prev => new Set([...prev, 4]));
    } catch (error) {
      console.error('Brainstorm error:', error);
      alert('Brainstorming failed. Please try again.');
    } finally {
      setIsBrainstorming(false);
    }
  };

  // Rewrite content
  const rewriteContent = async () => {
    if (!manuscript.trim()) {
      alert('Please enter content to rewrite!');
      return;
    }
    
    setIsRewriting(true);
    try {
      const response = await axios.post(`${API}/rewrite`, {
        content: manuscript,
        style: rewriteStyle,
        writing_type: writingType,
        chunk_index: 0,
      });

      setRewriteResult(response.data);
      setCompletedSteps(prev => new Set([...prev, 5]));
    } catch (error) {
      console.error('Rewrite error:', error);
      alert('Rewrite failed. Please try again.');
    } finally {
      setIsRewriting(false);
    }
  };

  // Continue rewriting with the next chunk (called when user clicks "Continue")
  const continueRewrite = async () => {
    if (!rewriteResult || rewriteResult.is_last) return;
    const nextIndex = rewriteResult.next_chunk_index;
    if (nextIndex === null || nextIndex === undefined) return;

    setIsRewriting(true);
    try {
      const response = await axios.post(`${API}/rewrite`, {
        content: manuscript,
        style: rewriteStyle,
        writing_type: writingType,
        chunk_index: nextIndex,
      });

      // Append rewritten text from the next chunk and update meta
      setRewriteResult(prev => ({
        ...response.data,
        rewritten: (prev.rewritten || '') + '\n\n' + (response.data.rewritten || ''),
        word_count_original: (prev.word_count_original || 0) + (response.data.word_count_original || 0),
        word_count_new: (prev.word_count_new || 0) + (response.data.word_count_new || 0),
        changes_made: [...(prev.changes_made || []), ...(response.data.changes_made || [])],
      }));
    } catch (error) {
      console.error('Continue rewrite error:', error);
      alert('Could not continue. Try again?');
    } finally {
      setIsRewriting(false);
    }
  };

  // Generate query letter
  const generateQueryLetter = async () => {
    try {
      const response = await axios.post(`${API}/query/generate`, {
        title: title || 'My Manuscript',
        genre: writingType,
        word_count: manuscript ? manuscript.split(' ').length.toString() : '0',
        synopsis: manuscript ? manuscript.substring(0, 500) : 'No manuscript uploaded',
        author_name: 'Author'
      });
      
      setQueryLetter(response.data.query_letter);
    } catch (error) {
      console.error('Query letter error:', error);
    }
  };

  // Search publishers
  const searchPublishers = async () => {
    try {
      const response = await axios.post(`${API}/publishers/search`, {
        genre: writingType,
        location: '',
        manuscript_type: 'book'
      });
      
      setPublishers(response.data.publishers);
      setCompletedSteps(prev => new Set([...prev, 9]));
    } catch (error) {
      console.error('Publisher search error:', error);
    }
  };

  // Download manuscript
  const downloadManuscript = (format) => {
    if (!manuscript.trim()) {
      alert('No manuscript to download!');
      return;
    }
    
    let content = manuscript;
    let filename = `${title || 'manuscript'}.${format}`;
    let type = 'text/plain';
    
    if (format === 'html') {
      content = `<!DOCTYPE html><html><head><title>${title || 'Manuscript'}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:50px auto;padding:20px;line-height:1.8;}</style></head><body><h1>${title || 'Manuscript'}</h1><div>${manuscript.replace(/\n/g, '<br>')}</div></body></html>`;
      type = 'text/html';
    }
    
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // TTS - Using browser's built-in speech synthesis (free)
  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text.substring(0, 5000));
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    } else {
      alert('Text-to-speech not supported in your browser');
    }
  };

  // STT - Using browser's built-in speech recognition (free)
  const startSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      
      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setManuscript(prev => prev + ' ' + transcript);
      };
      
      recognition.start();
      alert('Listening... Click anywhere to stop.');
      
      document.addEventListener('click', () => recognition.stop(), { once: true });
    } else {
      alert('Speech recognition not supported in your browser');
    }
  };

  return (
    <div className="scribeflow-app" data-testid="scribeflow-app">
      {showLanding && <LandingPage onEnterStudio={enterStudio} />}
      {!showLanding && (<>
      {/* Header */}
      <header className="app-header" data-testid="app-header">
        <button className="back-to-landing-btn" onClick={backToLanding} data-testid="back-to-landing">
          ← Back to Story
        </button>
        <h1 className="app-title">Scribe Flow Studio</h1>
        <p className="tagline">Where Art Meets Words</p>
        <p className="subtitle">A sanctuary for writers, poets, and creators to breathe, explore, and express</p>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* "How this studio works" — collapsible help panel */}
        <HowItWorks />

        {/* Step 1: Upload & Input */}
        <CollapsibleSection 
          title="Upload Your Masterpiece" 
          icon={Upload} 
          step={1}
          defaultOpen={true}
          completed={completedSteps.has(1)}
        >
          <div className="step-inner">
            <div className="writing-type-selector">
              <label>What are you writing?</label>
              <div className="type-grid" data-testid="writing-type-grid">
                {WRITING_TYPES.map(type => (
                  <button
                    key={type.id}
                    className={`type-btn ${writingType === type.id ? 'active' : ''}`}
                    onClick={() => setWritingType(type.id)}
                    data-testid={`type-${type.id}`}
                  >
                    <type.icon size={20} />
                    <span>{type.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="input-group">
              <label>Title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter your manuscript title..."
                className="title-input"
                data-testid="title-input"
              />
            </div>
            
            <div className="manuscript-input-area">
              <div className="input-header">
                <label>Your Writing</label>
                <div className="input-actions">
                  <label className={`upload-btn ${isUploading ? 'uploading' : ''}`}>
                    {isUploading ? (
                      <>
                        <Loader2 size={16} className="spinning" />
                        <span>Importing...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Upload (DOCX/PDF/TXT)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept=".txt,.docx,.pdf,.rtf"
                      onChange={handleFileUpload}
                      hidden
                      disabled={isUploading}
                      data-testid="file-upload"
                    />
                  </label>
                  <button className="voice-btn" onClick={startSpeechRecognition} data-testid="voice-input-btn">
                    <Mic size={16} />
                    <span>Voice Input</span>
                  </button>
                </div>
              </div>
              {uploadError && (
                <div className="upload-error" data-testid="upload-error">
                  <AlertCircle size={16} />
                  <span>{uploadError}</span>
                </div>
              )}
              <textarea
                value={manuscript}
                onChange={(e) => setManuscript(e.target.value)}
                placeholder="This is your space. 

Bring what you've written — finished or unfinished, polished or raw. 
Paste your words here, upload a file, or speak them aloud.

There's no judgment here. Only possibility."
                className="manuscript-textarea"
                data-testid="manuscript-textarea"
              />
              <div className="word-count">
                <span>{manuscript.split(/\s+/).filter(w => w).length.toLocaleString()} words</span>
                <span>{manuscript.length.toLocaleString()} characters</span>
              </div>
            </div>
            
            <ChatBox step="upload" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 2: Analysis */}
        <CollapsibleSection 
          title="AI Analysis" 
          icon={Sparkles} 
          step={2}
          completed={completedSteps.has(2)}
        >
          <div className="step-inner">
            <div className="analysis-controls">
              <button 
                className="primary-btn"
                onClick={analyzeManuscript}
                disabled={isAnalyzing || !manuscript.trim()}
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="spinning" size={18} />
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Wand2 size={18} />
                    <span>Analyze My Writing</span>
                  </>
                )}
              </button>
            </div>
            
            {analysisResult && (
              <div className="analysis-results" data-testid="analysis-results">
                <div className="score-card">
                  <div className="overall-score">
                    <span className="score-value">{analysisResult.overall_score || 7}</span>
                    <span className="score-label">/10</span>
                  </div>
                  <span className="score-title">Overall Score</span>
                </div>
                
                <div className="analysis-grid">
                  {['structure', 'character', 'style', 'grammar', 'plot'].map(category => (
                    <div key={category} className="analysis-card">
                      <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                      <div className="category-score">
                        {analysisResult[`${category}_analysis`]?.score || 7}/10
                      </div>
                      <p>{analysisResult[`${category}_analysis`]?.notes || 'Analysis pending...'}</p>
                    </div>
                  ))}
                </div>
                
                <div className="feedback-section">
                  <div className="strengths">
                    <h4><CheckCircle size={18} /> Strengths</h4>
                    <ul>
                      {(analysisResult.strengths || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="improvements">
                    <h4><AlertCircle size={18} /> Areas to Improve</h4>
                    <ul>
                      {(analysisResult.improvements || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            <ChatBox step="analysis" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 3: Character Builder */}
        <CollapsibleSection 
          title="Character Builder" 
          icon={Users} 
          step={3}
          completed={completedSteps.has(3)}
        >
          <div className="step-inner">
            <div className="character-form">
              <div className="input-group">
                <label>Character Name</label>
                <input
                  type="text"
                  value={characterName}
                  onChange={(e) => setCharacterName(e.target.value)}
                  placeholder="Enter character name..."
                  data-testid="character-name-input"
                />
              </div>
              <div className="input-group">
                <label>Additional Details (optional)</label>
                <textarea
                  value={characterDetails}
                  onChange={(e) => setCharacterDetails(e.target.value)}
                  placeholder="Age, role in story, key traits you want..."
                  data-testid="character-details-input"
                />
              </div>
              <button 
                className="primary-btn"
                onClick={generateCharacter}
                disabled={isGeneratingCharacter}
                data-testid="generate-character-btn"
              >
                {isGeneratingCharacter ? (
                  <>
                    <Loader2 className="spinning" size={18} />
                    <span>Creating Character...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>Generate Character</span>
                  </>
                )}
              </button>
            </div>
            
            {generatedCharacter && (
              <div className="character-profile" data-testid="character-profile">
                <h3>{generatedCharacter.name}</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <h4>Appearance</h4>
                    <p>{generatedCharacter.appearance}</p>
                  </div>
                  <div className="profile-item">
                    <h4>Personality</h4>
                    <div className="trait-tags">
                      {(generatedCharacter.personality || []).map((trait, i) => (
                        <span key={i} className="trait-tag">{trait}</span>
                      ))}
                    </div>
                  </div>
                  <div className="profile-item">
                    <h4>Background</h4>
                    <p>{generatedCharacter.background}</p>
                  </div>
                  <div className="profile-item">
                    <h4>Motivations</h4>
                    <p>{generatedCharacter.motivations}</p>
                  </div>
                  <div className="profile-item">
                    <h4>Fears & Conflicts</h4>
                    <p>{generatedCharacter.fears}</p>
                  </div>
                  <div className="profile-item">
                    <h4>Character Arc</h4>
                    <p>{generatedCharacter.arc}</p>
                  </div>
                </div>
              </div>
            )}
            
            <ChatBox step="characters" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 4: Brainstorm */}
        <CollapsibleSection 
          title="Creative Brainstorm" 
          icon={Lightbulb} 
          step={4}
          completed={completedSteps.has(4)}
        >
          <div className="step-inner">
            <div className="brainstorm-form">
              <div className="input-group">
                <label>What do you want to create?</label>
                <input
                  type="text"
                  value={brainstormTopic}
                  onChange={(e) => setBrainstormTopic(e.target.value)}
                  placeholder="e.g., A slam poetry piece about grocery price inequality..."
                  data-testid="brainstorm-topic-input"
                />
              </div>
              <div className="input-group">
                <label>Additional Context (optional)</label>
                <textarea
                  value={brainstormDetails}
                  onChange={(e) => setBrainstormDetails(e.target.value)}
                  placeholder="Any specific details, themes, or style you want..."
                  data-testid="brainstorm-details-input"
                />
              </div>
              <button 
                className="primary-btn"
                onClick={startBrainstorm}
                disabled={isBrainstorming}
                data-testid="brainstorm-btn"
              >
                {isBrainstorming ? (
                  <>
                    <Loader2 className="spinning" size={18} />
                    <span>Brainstorming...</span>
                  </>
                ) : (
                  <>
                    <Lightbulb size={18} />
                    <span>Spark My Creativity</span>
                  </>
                )}
              </button>
            </div>
            
            {brainstormResult && (
              <div className="brainstorm-results" data-testid="brainstorm-results">
                <div className="idea-card main-idea">
                  <h4>Main Concept</h4>
                  <p>{brainstormResult.main_idea}</p>
                </div>
                
                {brainstormResult.sample_piece && (
                  <div className="sample-piece">
                    <h4>Sample Piece (Use or Build From)</h4>
                    <div className="piece-content">
                      {brainstormResult.sample_piece}
                    </div>
                    <button 
                      className="secondary-btn"
                      onClick={() => setManuscript(prev => prev + '\n\n' + brainstormResult.sample_piece)}
                    >
                      Add to Manuscript
                    </button>
                  </div>
                )}
                
                {brainstormResult.variations?.length > 0 && (
                  <div className="variations">
                    <h4>Alternative Angles</h4>
                    <ul>
                      {brainstormResult.variations.map((v, i) => (
                        <li key={i}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            <ChatBox step="brainstorm" context={brainstormTopic} />
          </div>
        </CollapsibleSection>

        {/* Step 5: Polish & Transform */}
        <CollapsibleSection 
          title="Polish & Transform" 
          icon={Wand2} 
          step={5}
          completed={completedSteps.has(5)}
        >
          <div className="step-inner">
            <div className="polish-controls">
              <div className="style-selector">
                <label>Rewrite Style</label>
                <select 
                  value={rewriteStyle} 
                  onChange={(e) => setRewriteStyle(e.target.value)}
                  data-testid="rewrite-style-select"
                >
                  <option value="enhanced">Enhanced (Improve Flow)</option>
                  <option value="formal">Formal (Professional)</option>
                  <option value="creative">Creative (More Vivid)</option>
                  <option value="simplified">Simplified (Clearer)</option>
                  <option value="dramatic">Dramatic (More Impact)</option>
                </select>
              </div>
              
              <div className="action-buttons">
                <button 
                  className="primary-btn"
                  onClick={rewriteContent}
                  disabled={isRewriting || !manuscript.trim()}
                  data-testid="rewrite-btn"
                >
                  {isRewriting ? (
                    <>
                      <Loader2 className="spinning" size={18} />
                      <span>Rewriting...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      <span>Rewrite Content</span>
                    </>
                  )}
                </button>
                
                <button 
                  className="secondary-btn"
                  onClick={() => speakText(manuscript)}
                  disabled={!manuscript.trim()}
                  data-testid="tts-btn"
                >
                  <Volume2 size={18} />
                  <span>Listen (TTS)</span>
                </button>
              </div>
            </div>
            
            {rewriteResult && (
              <div className="rewrite-results" data-testid="rewrite-results">
                {/* Persona attribution */}
                {rewriteResult.persona_name && (
                  <div className="rewrite-persona-tag">
                    <span className="rewrite-persona-avatar">{rewriteResult.persona_name.charAt(0)}</span>
                    <span>
                      <strong>{rewriteResult.persona_name}</strong>
                      <em> · {rewriteResult.persona_title}</em>
                    </span>
                  </div>
                )}

                {/* Chunk progress */}
                {rewriteResult.total_chunks > 1 && (
                  <div className="rewrite-chunk-progress" data-testid="chunk-progress">
                    <div className="chunk-progress-bar">
                      <div
                        className="chunk-progress-fill"
                        style={{ width: `${(((rewriteResult.chunk_index || 0) + 1) / rewriteResult.total_chunks) * 100}%` }}
                      />
                    </div>
                    <div className="chunk-progress-label">
                      Section {(rewriteResult.chunk_index || 0) + 1} of {rewriteResult.total_chunks}
                      {rewriteResult.chunk_label && ` · ${rewriteResult.chunk_label}`}
                    </div>
                  </div>
                )}

                <div className="rewrite-comparison">
                  <div className="rewrite-panel">
                    <h4>Rewritten Version</h4>
                    <div className="rewrite-content">
                      {rewriteResult.rewritten}
                    </div>
                  </div>
                </div>

                <div className="rewrite-stats">
                  <span>Original: {rewriteResult.word_count_original} words</span>
                  <span>New: {rewriteResult.word_count_new} words</span>
                </div>

                {/* Continue / hand-off messaging from Aiden */}
                {rewriteResult.continuation_message && (
                  <div className={`rewrite-continuation ${rewriteResult.is_last ? 'done' : 'more'}`}>
                    <p>{rewriteResult.continuation_message}</p>
                  </div>
                )}

                <div className="rewrite-action-row">
                  {!rewriteResult.is_last && (
                    <button
                      className="primary-btn"
                      onClick={continueRewrite}
                      disabled={isRewriting}
                      data-testid="continue-rewrite-btn"
                    >
                      {isRewriting ? 'Rewriting next section...' : 'Continue with next section →'}
                    </button>
                  )}
                  <button
                    className="secondary-btn"
                    onClick={() => setManuscript(rewriteResult.rewritten)}
                    data-testid="apply-rewrite-btn"
                  >
                    Apply Rewrite
                  </button>
                </div>

                {/* Handoff to Audiobook (Sage) when the rewrite is fully complete */}
                {rewriteResult.is_last && (
                  <HandoffCard
                    fromModule="polish"
                    manuscriptSummary={(rewriteResult.rewritten || '').slice(0, 240)}
                    onAccept={(nextModuleId) => {
                      const target = document.querySelector(`[data-testid="step-${nextModuleId === 'audiobook' ? 6 : 7}"]`);
                      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  />
                )}
              </div>
            )}
            
            <div className="download-section">
              <h4>Export Your Work</h4>
              <p className="section-description">Download your manuscript in professional formats</p>
              <div className="download-buttons">
                <button className="download-btn docx" onClick={() => exportManuscript('docx')} data-testid="download-docx">
                  <FileText size={16} />
                  <span>DOCX</span>
                </button>
                <button className="download-btn pdf" onClick={() => exportManuscript('pdf')} data-testid="download-pdf">
                  <FileText size={16} />
                  <span>PDF</span>
                </button>
                <button className="download-btn txt" onClick={() => exportManuscript('txt')} data-testid="download-txt">
                  <Download size={16} />
                  <span>TXT</span>
                </button>
                <button className="download-btn" onClick={() => downloadManuscript('html')} data-testid="download-html">
                  <Download size={16} />
                  <span>HTML</span>
                </button>
              </div>
            </div>
            
            <ChatBox step="polish" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 6: Audiobook */}
        <CollapsibleSection 
          title="Create Audiobook" 
          icon={Headphones} 
          step={6}
          completed={completedSteps.has(6)}
        >
          <div className="step-inner">
            <AudiobookSection manuscript={manuscript} writingType={writingType} />
            <ChatBox step="audiobook" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 7: Translation */}
        <CollapsibleSection 
          title="Translate Your Work" 
          icon={Languages} 
          step={7}
          completed={completedSteps.has(7)}
        >
          <div className="step-inner">
            <TranslationSection 
              manuscript={manuscript} 
              onTranslationComplete={(translated, lang) => {
                setCompletedSteps(prev => new Set([...prev, 7]));
              }}
            />
            <ChatBox step="translation" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 8: Image Creator */}
        <CollapsibleSection 
          title="Create Cover Art & Inspired Imagery" 
          icon={Image} 
          step={8}
          completed={completedSteps.has(8)}
        >
          <div className="step-inner">
            <ImageCreatorSection 
              manuscript={manuscript}
              title={title}
              writingType={writingType}
            />
            <ChatBox step="image" context={manuscript} />
          </div>
        </CollapsibleSection>

        {/* Step 9: Publish */}
        <CollapsibleSection 
          title="Publish Your Work" 
          icon={BookOpen} 
          step={9}
          completed={completedSteps.has(9)}
        >
          <div className="step-inner">
            <div className="publish-actions">
              <button 
                className="primary-btn"
                onClick={generateQueryLetter}
                data-testid="query-letter-btn"
              >
                <FileText size={18} />
                <span>Generate Query Letter</span>
              </button>
              
              <button 
                className="secondary-btn"
                onClick={searchPublishers}
                data-testid="find-publishers-btn"
              >
                <BookOpen size={18} />
                <span>Find Publishers</span>
              </button>
            </div>
            
            {queryLetter && (
              <div className="query-letter" data-testid="query-letter">
                <h4>Your Query Letter</h4>
                <div className="letter-content">
                  {queryLetter}
                </div>
                <button 
                  className="secondary-btn"
                  onClick={() => navigator.clipboard.writeText(queryLetter)}
                >
                  Copy to Clipboard
                </button>
              </div>
            )}
            
            {publishers.length > 0 && (
              <div className="publishers-list" data-testid="publishers-list">
                <h4>Publishers for Your Genre</h4>
                <div className="publishers-grid">
                  {publishers.map((pub, i) => (
                    <div key={i} className="publisher-card">
                      <h5>{pub.name}</h5>
                      <span className="pub-type">{pub.type}</span>
                      <p>{pub.accepts}</p>
                      {pub.response_time && <p className="response-time">Response: {pub.response_time}</p>}
                      <a href={pub.website} target="_blank" rel="noopener noreferrer" className="pub-link">
                        Visit Website
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <ChatBox step="publish" context={manuscript} />
          </div>
        </CollapsibleSection>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-logo">
          <span className="footer-logo-text">Scribe Flow Studio</span>
        </div>
        <p className="footer-sub">Built with love for writers everywhere</p>

        <div className="footer-feedback">
          <p className="footer-feedback-text">
            Your feedback is very important to us — good, bad, or indifferent. Please take a moment
            to leave a review. We take every comment, good or critical, seriously and want to hear from you!
          </p>
          <div className="footer-actions">
            <button
              type="button"
              onClick={() => backToLanding('reviews')}
              className="footer-link-btn footer-link-review"
              data-testid="footer-leave-review"
            >
              ★ Leave a Review
            </button>
            <a
              href="mailto:aaron@aiquires.com?subject=Scribe%20Flow%20Studio%20Contact%20Us%20Email"
              className="footer-link-btn footer-link-contact"
              data-testid="footer-contact-us"
            >
              ✉ Contact Us
            </a>
          </div>
        </div>
      </footer>
      </>)}
    </div>
  );
}

export default App;
